import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  type ChatInteractionMode,
  type ChatContextItem,
  ChatContextType,
  isBrandInsightsContext,
} from '@trinity/chat';
import { AuraChatContext, SidePanelExpandedContext } from '@trinity/chat-components/components/BrandInsights/context.js';
import { ChatInstance } from '@trinity/chat-components/components/Chat/Chat.js';
import {
  ActiveChatSessionContext,
  ActivePageContext,
  ChatContextItemsContext,
  ChatFaqContextItemContext,
  ChatSettingsToggleContext,
} from '@trinity/chat-components/components/Chat/context.js';
import { KolWelcome } from '@trinity/chat-components/components/Chat/Kol.Welcome.js';
import {
  GetStartedContext,
  ChatGetStarted,
} from '@trinity/chat-components/components/Chat.GetStarted/index.js';
import { ChatHeader } from '@trinity/chat-components/components/Chat.Header/index.js';
import { ChatSettings } from '@trinity/chat-components/components/Chat.Screen/Chat.Setting.js';
import { ChatSidePanel } from '@trinity/chat-components/components/Chat.SidePanel/SidePanel.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context.js';
import { ChatWidget } from '@trinity/chat-components/components/Chat.Widget/index.js';
import {
  AvatarPanelContext,
  useAvatarPanel,
} from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context.js';
import { HeyGenPanel } from '@trinity/chat-components/components/DigitalTwin/HeyGenPanel.js';
import { HeyGenResponseBridge } from '@trinity/chat-components/components/LiveAvatar/HeyGenResponseBridge.js';
import { LiveAvatar } from '@trinity/chat-components/components/LiveAvatar/LiveAvatar.js';
import { LiveAvatarSessionManager } from '@trinity/chat-components/components/LiveAvatar/LiveAvatarSessionManager.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { ChatWelcomeScreenContext } from '@trinity/chat-components/containers/BrandInsights/Context.js';
import {
  useChatInteractionModes,
  useChatLiveAvatarOptions,
} from '@trinity/chat-components/containers/Chat/selectors.js';
import { useMonitorChatIdentifier } from '@trinity/chat-components/containers/Chat/useMonitorHashChatIdentifier.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import chatBackgroundImg from '@trinity/chat-components/static/images/chat-background.png';
import { FaqSwitchContext } from '@/contexts/FaqSwitchContext';
import { ChatScreenContainer } from './Container';
import { MessageGenerationWrapper } from './MessageGenerationWrapper';

const BrandInsightsChatScreen = () => {
  const [videoExpanded, setVideoExpanded] = useState(false);

  const settingsInteractionModes = useChatInteractionModes();

  const [interactionMode, setInteractionMode] = useState<ChatInteractionMode>(
    settingsInteractionModes.default
  );

  useMonitorChatIdentifier();
  useRegisterFaqSwitch();

  const isLoading = useIsChatLoading();

  if (isLoading) {
    return null;
  }

  return (
    <Root>
      <MainChat>
        <AvatarPanelContext.Provider
          value={{
            expanded: videoExpanded,
            setExpanded: setVideoExpanded,
            isDigitalTwin: interactionMode === 'video',
            isNewChatInteractionLayout: true,
            availableModes: settingsInteractionModes.available,
            defaultInteractionMode: settingsInteractionModes.default,
            interactionMode,
            setInteractionMode,
            segmentIconUrl: undefined,
            showAuthor: interactionMode !== 'video',
          }}
        >
          {interactionMode === 'video' && <ChatVideoInteraction />}
          {interactionMode !== 'video' && <ChatTextOrVoiceInteraction />}
        </AvatarPanelContext.Provider>
      </MainChat>
    </Root>
  );
};

const ChatVideoInteraction = () => {
  const [settings] = useContext(ChatSettingsToggleContext);
  const { theme } = useContext(AppThemingContext);
  const [sessionKey, setSessionKey] = useState(0);
  const { setExpanded, expanded } = useAvatarPanel();
  const settingsAvatarOptions = useChatLiveAvatarOptions();
  const shrinkForFab = useShouldShowSidePanelReopen();

  const style = theme?.enabled
    ? {
        backgroundImage: 'none',
        backgroundColor: '#fafafa',
      }
    : {};

  const showSettings = !!settings;
  const showChat = !showSettings;

  const avatarId = settingsAvatarOptions?.avatarId;
  const ttsConfig = settingsAvatarOptions?.ttsConfig;
  const sessionActive = !!avatarId;

  const handleToggleExpand = () => setExpanded(!expanded);
  const handleReinitialize = useCallback(() => setSessionKey((k) => k + 1), []);

  return (
    <>
      <Wrap style={style} shrinkForFab={shrinkForFab}>
        {!showChat ? (
          <ChatSettings />
        ) : (
          <VideoWrap expanded={expanded}>
            <LiveAvatarSessionManager key={sessionKey} avatarId={avatarId} ttsConfig={ttsConfig}>
              <HeyGenPanel
                expanded={expanded}
                onToggleExpand={handleToggleExpand}
                sessionActive={sessionActive}
                onReinitialize={handleReinitialize}
              >
                <LiveAvatar />
              </HeyGenPanel>
              <HeyGenResponseBridge />
            </LiveAvatarSessionManager>
            <ChatInteractionWrapper>
              <ChatHeader />
              <ChatInstance hideGuidelinesLink />
            </ChatInteractionWrapper>
          </VideoWrap>
        )}
      </Wrap>
      <ChatSidePanel hideExpandButton />
      <SidePanelReopenWidget />
    </>
  );
};

function computeChatVisibility(
  settings: boolean,
  enabled: boolean,
  visible: boolean,
  brandInsights: boolean,
  isFaq: boolean,
  getStarted: boolean,
  auraChatStatus: boolean
) {
  const showSettings = settings;
  const showWelcomeScreen = !showSettings && enabled && visible && !brandInsights && !isFaq;
  const showGetStarted = !showSettings && getStarted && !enabled;
  const showAuraChat = !showSettings && !showWelcomeScreen && !showGetStarted && auraChatStatus;
  const showChat = !showSettings && !showWelcomeScreen && !showGetStarted;
  const showHeader = !showSettings && !showAuraChat;
  return { showSettings, showWelcomeScreen, showGetStarted, showAuraChat, showChat, showHeader };
}

const ChatTextOrVoiceInteraction = () => {
  const [getStarted, dismissGetStarted] = useContext(GetStartedContext);
  const { dismiss, enabled, visible } = useContext(ChatWelcomeScreenContext);
  const { auraChatStatus } = useContext(AuraChatContext);
  const [settings] = useContext(ChatSettingsToggleContext);
  const { theme } = useContext(AppThemingContext);
  const { context } = useContext(ActiveChatSessionContext);
  const faqContext = useContext(ChatFaqContextItemContext);

  const brandInsights = isBrandInsightsContext(context);
  const isFaq =
    faqContext != null &&
    context.type === ChatContextType.Agent &&
    (context.data as ChatContextItem | undefined)?.identifier === faqContext.identifier;

  const style = theme?.enabled
    ? {
        backgroundImage: 'none',
        backgroundColor: '#fafafa',
      }
    : {};

  const { showSettings, showWelcomeScreen, showGetStarted, showAuraChat, showChat, showHeader } = computeChatVisibility(
    !!settings,
    enabled,
    visible,
    brandInsights,
    isFaq,
    getStarted,
    auraChatStatus
  );
  const shrinkForFab = useShouldShowSidePanelReopen();

  return (
    <>
      <Wrap style={style} shrinkForFab={shrinkForFab}>
        {showHeader && <ChatHeader />}

        {showSettings && <ChatSettings />}
        {showWelcomeScreen && <KolWelcome onClick={dismiss} />}
        {showGetStarted && <StyledGetStarted onClick={dismissGetStarted} />}
        {showAuraChat && (
          <iframe
            src="https://realinsightsauras.trinitylifesciences.com/home/hcp_auras"
            width="100%"
            height="100%"
            allow="microphone"
            frameBorder="0"
          ></iframe>
        )}
        {showChat && (
          <MessageGenerationWrapper>
            <ChatInstance hideGuidelinesLink />
          </MessageGenerationWrapper>
        )}
      </Wrap>

      <ChatSidePanel hideExpandButton />
      <SidePanelReopenWidget />
    </>
  );
};

const SidePanelReopenWidget = () => {
  const [, setIsExpanded] = useContext(SidePanelExpandedContext);
  const showReopenWidget = useShouldShowSidePanelReopen();

  const handleClick = useCallback(() => setIsExpanded(true), [setIsExpanded]);

  if (!showReopenWidget) return null;

  return <ChatWidget onClick={handleClick} />;
};

// Single source of truth for both the lightbulb FAB's visibility and the main chat
// window's "shrink to reveal the FAB" behavior below — kept in lockstep by construction.
function useShouldShowSidePanelReopen(): boolean {
  const { pageId, layout } = useContext(ActivePageContext);
  const [isExpanded] = useContext(SidePanelExpandedContext);

  return pageId !== null && layout === 'side_chat' && !isExpanded;
}

function useRegisterFaqSwitch() {
  const { setTrigger } = useContext(FaqSwitchContext);
  const faqContext = useContext(ChatFaqContextItemContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();

  useEffect(() => {
    if (!faqContext || !brandIdentifier) {
      return;
    }
    setTrigger(() => {
      dispatch({
        type: 'instance-context-changed',
        payload: {
          context: { type: ChatContextType.Agent, data: faqContext, metadata: {} },
          brandIdentifier,
        },
      });
    });
    return () => setTrigger(null);
  }, [faqContext, brandIdentifier, dispatch, setTrigger]);
}

function useIsChatLoading() {
  const contextItems = useContext(ChatContextItemsContext);
  const chat = useContext(ActiveChatSessionContext);

  return useMemo(() => {
    return !contextItems || contextItems.length === 0 || !chat.context;
  }, [contextItems, chat.context]);
}

export const ChatScreen = () => {
  return (
    <ChatScreenContainer>
      <BrandInsightsChatScreen />
    </ChatScreenContainer>
  );
};

const Root = styled.div({
  height: `calc(100vh - var(--navbar-height))`,
  width: '100%',
});

const Wrap = styled('div', { shouldForwardProp: (prop) => prop !== 'shrinkForFab' })<{ shrinkForFab?: boolean }>(
  ({ shrinkForFab }) => ({
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    background: `url(${chatBackgroundImg}) no-repeat`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    transition: 'width 300ms ease, margin 300ms ease',
    ...(shrinkForFab && {
      '@media (min-width: 901px)': {
        width: '70%',
        margin: '0 auto',
      },
    }),
  })
);

const StyledGetStarted = styled(ChatGetStarted)({
  marginTop: 30,
});

const MainChat = styled.div({
  display: 'flex',
  boxSizing: 'border-box',
  width: '100%',
  height: '100%',
  // height: `calc(100% - ${FooterHeight}px)`,
});

const VideoWrap = styled('div', {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded }) => ({
  display: 'grid',
  gridTemplateColumns: expanded ? '1fr 0' : '9fr 11fr',
  flexGrow: 1,
  height: '100%',
  overflow: 'hidden',
}));

const ChatInteractionWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  height: '100%',
  overflow: 'hidden',
});
